/**
 * AI CONTEXT
 * Purpose: generic mutation hooks for Firestore docs.
 * useSaveDoc: create-or-update in one transaction (no separate exists check).
 * useUpdateDoc: update-only; fails if the doc does not exist.
 * Exposes: saveDoc / updateDoc + loading/error state.
 * Used by: forms and mutation flows across modules.
 */
import { useCallback, useState } from "react"

import Firebase from "#classes/Firebase"
import { useUserContext } from "#contexts/UserContextProvider"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import { DatabaseObject, GenericObject } from "#customTypes/types"
import useErrorModal from "#hooks/useErrorModal"
import { camelToWords } from "#utils/textFormatters"

interface UseSaveDocReturn<T extends DatabaseObject> {
  saveDoc: (data: Partial<T> & { id?: Uuid }) => Promise<Uuid | null>
  isLoading: boolean
  error: Error | null
}

interface UseUpdateDocReturn {
  updateDoc: (id: Uuid, patch: GenericObject) => Promise<Uuid | null>
  isLoading: boolean
  error: Error | null
}

const useMutationState = (docType: DOC_TYPES) => {
  const [error, setError] = useState<Error | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const openErrorModal = useErrorModal(`saving a ${camelToWords(docType)}`)

  const run = useCallback(
    async (write: () => Promise<Uuid>): Promise<Uuid | null> => {
      setIsLoading(true)
      setError(null)
      try {
        const id = await write()
        setIsLoading(false)
        return id
      } catch (caught) {
        const err = caught instanceof Error ? caught : new Error(String(caught))
        setIsLoading(false)
        setError(err)
        openErrorModal(err, `Failed to save ${docType}: ${err.message}`)
        return null
      }
    },
    [docType, openErrorModal]
  )

  return { run, isLoading, error }
}

/**
 * Hook for creating or updating documents in Firebase
 * - If data has no id, creates a new document
 * - If data has an id, updates it when it exists and creates it otherwise,
 *   decided inside one transaction
 * - Returns the document id on success, null on failure (after an error modal)
 */
export function useSaveDoc<T extends DatabaseObject>(
  docType: DOC_TYPES
): UseSaveDocReturn<T> {
  const { currentUserId } = useUserContext()
  const { run, isLoading, error } = useMutationState(docType)

  const saveDoc = useCallback(
    (data: Partial<T> & { id?: Uuid }): Promise<Uuid | null> => {
      data.lastUpdatedBy = currentUserId!
      data.lastUpdatedAt = new Date().toISOString()
      return run(() => Firebase.getInstance().upsertDocument<T>(docType, data))
    },
    [docType, currentUserId, run]
  )

  return { saveDoc, isLoading, error }
}

/**
 * Update-only counterpart to useSaveDoc. Takes dot-path keys
 * (e.g. `artwork.{id}.rotation`) and FieldValues (arrayUnion, deleteField) so
 * callers write only what they change instead of spreading a subscription
 * snapshot back over the doc.
 */
export function useUpdateDoc(docType: DOC_TYPES): UseUpdateDocReturn {
  const { run, isLoading, error } = useMutationState(docType)

  const updateDoc = useCallback(
    (id: Uuid, patch: GenericObject): Promise<Uuid | null> =>
      run(() =>
        Firebase.getInstance().updateDocumentOrThrow(docType, id, patch)
      ),
    [docType, run]
  )

  return { updateDoc, isLoading, error }
}
